/**
 * Comparison slider — syncs the range input to --compare (CSS clip-path).
 * Pauses the parent Flickity autoplay while the handle is dragged.
 * First section enter plays a CSS hint once on the selected slide, then stops.
 * When data-link is set: image clicks use the overlay <a>; only the handle drags.
 */
const comparisonSectionHints = new WeakMap();

function comparisonSelectedSlider(section) {
  return (
    section.querySelector('.carousel__slide.is-selected:not(.clone) comparison-slider-component') ||
    section.querySelector('.carousel__slide:not(.clone) comparison-slider-component') ||
    section.querySelector('comparison-slider-component')
  );
}

function comparisonPlayHintOnce(section) {
  const state = comparisonSectionHints.get(section);
  if (!state || state.played || state.reducedMotion) return;
  state.played = true;
  if (state.observer) {
    state.observer.disconnect();
    state.observer = null;
  }

  const target = comparisonSelectedSlider(section);
  if (!target || typeof target.playHint !== 'function') return;
  target.playHint();
}

function comparisonEnsureSectionHint(section) {
  if (comparisonSectionHints.has(section)) return;

  const state = {
    played: false,
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  };
  state.observer = new IntersectionObserver(
    (entries) => {
      if (!(entries[0] && entries[0].isIntersecting)) return;
      comparisonPlayHintOnce(section);
    },
    { threshold: 0.35 }
  );
  state.observer.observe(section);
  comparisonSectionHints.set(section, state);
}

if (!customElements.get('comparison-slider-component')) {
  class ComparisonSliderComponent extends HTMLElement {
    connectedCallback() {
      this.mediaWrapper = this.querySelector('[data-comparison-slider]');
      this.slider = this.querySelector('.cs-slider');
      this.handle = this.querySelector('.cs-slider__handle');
      this.carousel = this.closest('.carousel');
      this.section = this.closest('.comparison-slider-section');
      if (!this.mediaWrapper || !this.slider) return;

      this.hasLink = Boolean(this.dataset.link);

      this.onInput = () => {
        this.setCompare(this.slider.value);
      };

      if (this.hasLink && this.handle) {
        this.bindHandleDrag();
      } else {
        this.bindRangeDrag();
      }

      const slide = this.closest('.carousel__slide');
      if (this.section && !(slide && slide.classList.contains('clone'))) {
        comparisonEnsureSectionHint(this.section);
      }
    }

    disconnectedCallback() {
      if (this.mediaWrapper && this.onIntroEnd) {
        this.mediaWrapper.removeEventListener('animationend', this.onIntroEnd);
      }
      this.unbindRangeDrag();
      this.unbindHandleDrag();
    }

    bindRangeDrag() {
      this.onPointerDown = () => {
        this._dragging = true;
        this.finishIntro();
        this.setCompare(this.slider.value);
        const flkty = this.flickity();
        if (flkty) flkty.stopPlayer();
      };
      this.onPointerUp = () => {
        this._dragging = false;
        const flkty = this.flickity();
        if (flkty) flkty.playPlayer();
      };

      this.slider.addEventListener('input', this.onInput);
      this.slider.addEventListener('pointerdown', this.onPointerDown);
      this.slider.addEventListener('pointerup', this.onPointerUp);
      this.slider.addEventListener('pointercancel', this.onPointerUp);
    }

    unbindRangeDrag() {
      if (!this.slider || !this.onPointerDown) return;
      this.slider.removeEventListener('input', this.onInput);
      this.slider.removeEventListener('pointerdown', this.onPointerDown);
      this.slider.removeEventListener('pointerup', this.onPointerUp);
      this.slider.removeEventListener('pointercancel', this.onPointerUp);
    }

    bindHandleDrag() {
      this.onHandleDown = (event) => {
        if (event.button != null && event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        this._dragging = true;
        this.finishIntro();

        const rect = this.mediaWrapper.getBoundingClientRect();
        this._dragRect = {
          left: rect.left,
          width: rect.width || 1,
        };
        this.updateCompareFromClientX(event.clientX);

        this.handle.setPointerCapture(event.pointerId);
        const flkty = this.flickity();
        if (flkty) flkty.stopPlayer();
      };

      this.onHandleMove = (event) => {
        if (!this._dragging || !this._dragRect) return;
        event.preventDefault();
        this.updateCompareFromClientX(event.clientX);
      };

      this.onHandleUp = (event) => {
        if (!this._dragging) return;
        event.preventDefault();
        event.stopPropagation();
        this._dragging = false;
        this._dragRect = null;
        if (this.handle.hasPointerCapture?.(event.pointerId)) {
          this.handle.releasePointerCapture(event.pointerId);
        }
        const flkty = this.flickity();
        if (flkty) flkty.playPlayer();
      };

      this.onHandleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
      };

      this.onIntroEnd = (event) => {
        if (event.animationName !== 'comparison-swipe-right') return;
        this.finishIntro();
      };

      this.handle.addEventListener('pointerdown', this.onHandleDown);
      this.handle.addEventListener('pointermove', this.onHandleMove);
      this.handle.addEventListener('pointerup', this.onHandleUp);
      this.handle.addEventListener('pointercancel', this.onHandleUp);
      this.handle.addEventListener('click', this.onHandleClick);
    }

    unbindHandleDrag() {
      if (!this.handle || !this.onHandleDown) return;
      this.handle.removeEventListener('pointerdown', this.onHandleDown);
      this.handle.removeEventListener('pointermove', this.onHandleMove);
      this.handle.removeEventListener('pointerup', this.onHandleUp);
      this.handle.removeEventListener('pointercancel', this.onHandleUp);
      this.handle.removeEventListener('click', this.onHandleClick);
    }

    updateCompareFromClientX(clientX) {
      if (!this._dragRect) return;
      const ratio = (clientX - this._dragRect.left) / this._dragRect.width;
      const value = Math.max(0, Math.min(100, Math.round(ratio * 100)));
      this.setCompare(value);
    }

    setCompare(value) {
      const next = String(value);
      this.slider.value = next;
      this.mediaWrapper.style.setProperty('--compare', next);
    }

    playHint() {
      if (this._dragging) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const slide = this.closest('.carousel__slide');
      if (slide && slide.classList.contains('clone')) return;
      if (!this.mediaWrapper || !this.slider) return;

      this.classList.remove('is-intro-done', 'is-in-view');
      this.setCompare(50);
      this.mediaWrapper.style.removeProperty('--compare');
      this.mediaWrapper.removeEventListener('animationend', this.onIntroEnd);
      this.onIntroEnd = (event) => {
        if (event.animationName !== 'comparison-swipe-right') return;
        this.finishIntro();
      };
      this.mediaWrapper.addEventListener('animationend', this.onIntroEnd);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this._dragging) return;
          this.classList.add('is-in-view');
        });
      });
    }

    finishIntro() {
      this.classList.add('is-intro-done');
      if (this.mediaWrapper && this.onIntroEnd) {
        this.mediaWrapper.removeEventListener('animationend', this.onIntroEnd);
      }
    }

    flickity() {
      if (!this.carousel || typeof Flickity === 'undefined') return null;
      return Flickity.data(this.carousel);
    }
  }

  customElements.define('comparison-slider-component', ComparisonSliderComponent);
}
